import React, { createContext, useContext, useState, useEffect } from 'react';
import { setAuthToken, setLogoutCallback } from '../api/client';

const AuthContext = createContext(null);

// ── Eagerly restore the token so every axios request made during the first
//    render already has the Authorization header set.  This avoids a race
//    condition where child components fire their own useEffect API calls
//    before AuthProvider's useEffect has had a chance to run setAuthToken().
(function restoreTokenEagerly() {
  try {
    const token = localStorage.getItem('token');
    if (token) setAuthToken(token);
  } catch (_) {}
})();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Token is already set eagerly above; this effect is kept as a safety net
    // in case the module-level IIFE ran before localStorage was available
    // (e.g. SSR environments).
    const token = localStorage.getItem('token');
    if (token) setAuthToken(token);
    setLoading(false);
  }, []);

  const login = async (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setAuthToken(token);
    setUser(userData);
  };

  const logout = async () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setAuthToken(null);
    setUser(null);
  };

  // Update user data in state + localStorage (e.g. after password change clears must_change_password)
  const updateUser = (updatedUser) => {
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  useEffect(() => {
    setLogoutCallback(() => { setUser(null); });
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
